import { isPlatformBrowser, isPlatformServer, Location } from '@angular/common';
import { Inject, Injectable, Optional, PLATFORM_ID, signal } from '@angular/core';
import { NavigationEnd, NavigationStart, Params, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { IQuoteControllerService } from '../models/interfaces/quote-controller-service';
import { ERROR_CONFIG } from '../globals/errors/error-dialog.service';
import { ErrorDialogComponent } from '../globals/errors/error-dialog/error-dialog.component';
import { ModalService } from '../globals/modals/modal.service';
import { generateRouteFromUrl } from '../helpers/route-helper';
import { QuoteFlowType } from '../enums/quote-flow-type';
import { Route } from '../store/cms/models/route';
import {
    selectActiveIndex,
    selectActivePage,
    selectNextPage,
    selectPageByRoute,
    selectPageForm,
    selectPageType,
} from '../store/quote-controller/quote-controller.selectors';
import { Condition } from '../store/cms/models/condition';
import {
    navigateToRoute,
    reset,
    setABTestVariantsValues,
} from '../store/quote-controller/quote-controller.actions';
import { DropOffPageState } from '../enums/drop-off-page-state';
import { selectIsLoading } from '../store/cms/cms.selectors';
import { DynamicPageIndex } from '../models/dynamic-page-index';
import { AppConfigService } from './app-config.service';
import { LoggingService } from './logging.service';
import { ResolveStoreValueService } from './resolve-store-value.service';

@UntilDestroy()
@Injectable()
export abstract class BaseQuoteControllerService implements IQuoteControllerService {
    public loading$ = this.store$.select(selectIsLoading);
    public activePage$ = this.store$.select(selectActivePage);
    public activePage = this.store$.selectSignal(selectActivePage);
    public nextPage = this.store$.selectSignal(selectNextPage);
    public pageData = this.store$.selectSignal(selectPageForm);
    public abstract queueIsBusy$: Observable<boolean>;
    public pageType? = this.store$.selectSignal(selectPageType);
    public abstract leadNumber: any;
    public abstract vdn: any;
    public anchorPointIsLoading$: Subject<boolean> = new Subject<boolean>();
    public abstract navigationService: any;

    public activeIndex: DynamicPageIndex;
    public url: string;
    public route: Route;
    public quoteInitialised: boolean = false;
    public isDropOff: boolean = false;

    public hideBackButton = signal(false);
    public showPremiumBanner = signal(false);
    public slidePremiumBanner = signal(false);
    public hideTrackbar = signal(false);
    public promotionDisclaimer = signal(false);
    public minifyTrackbar = signal(false);
    public applyTransparentHeader = signal(false);

    constructor(
        protected readonly appConfig: AppConfigService,
        protected readonly store$: Store,
        protected readonly router: Router,
        protected readonly modalService: ModalService,
        protected readonly location: Location,
        protected readonly loggingService: LoggingService,
        public resolveStoreValueService$: ResolveStoreValueService,
        @Inject(PLATFORM_ID) protected readonly platformId: Object,
        @Optional() @Inject(ERROR_CONFIG) errorConfig: any,
    ) {
        this.route = generateRouteFromUrl(this.router.url, this.platformId);
        this.isDropOff = this.router.url.includes('callback');

        this.handleBackNextRoutingEvents();

        router.events
            .pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                untilDestroyed(this),
            )
            .subscribe((routerEvent) => {
                this.isDropOff = this.router.url.includes('callback');
                this.processRouterEvent(routerEvent);
                this.checkFlowType();

                if (isPlatformServer(this.platformId)) {
                    this.createCanonicalLink();
                } else {
                    window?.scrollTo(0, 0);
                }
            });

        this.store$
            .select(selectActiveIndex)
            .pipe(untilDestroyed(this))
            .subscribe((index) => {
                this.activeIndex = index;
            });

        this.errorConfig(errorConfig);
    }

    /**
     * Get the starting route
     * @returns {Route} - The starting route
     */
    public getStartingRoute(): Route {
        const baseRoute = this.appConfig.config().defaultBaseRoute;
        // This gets hit when we start the flow from scratch, i.e. nothing in localstorage and route is empty
        return {
            baseRoute: baseRoute,
            sectionRoute: '',
            pageRoute: '',
        };
    }

    /**
     * Handles back and next routing events.
     *
     * This method subscribes to the router's events and listens for `NavigationStart` events.
     * When a `NavigationStart` event is detected, it checks if the route is restored from the browser history
     * (i.e., the user clicked the back or forward button). If so, it generates the route from the URL and
     * navigates to ensure the page gets re-rendered on the previous route.
     */
    private handleBackNextRoutingEvents() {
        this.router.events
            .pipe(
                filter((e): e is NavigationStart => e instanceof NavigationStart),
                untilDestroyed(this),
            )
            .subscribe((routerEvent) => {
                if (routerEvent.restoredState) {
                    const route = generateRouteFromUrl(routerEvent.url, this.platformId);
                    this.navigate(route);
                }
            });
    }

    /**
     * Processes the router event.
     *
     * This method is called when a `NavigationEnd` event is detected. It updates the current URL and
     * generates a new route based on the URL after redirects.
     *
     * @param {NavigationEnd} event - The navigation end event containing the URL after redirects.
     */
    public processRouterEvent(event: NavigationEnd): void {
        this.url = event.urlAfterRedirects;

        this.route = generateRouteFromUrl(event.urlAfterRedirects, this.platformId);

        //Submit Page navigation end events to datalayer
        this.submitPageNavigationToDataLayer(event);
    }

    /**
     * Check flow type and update if necessary
     */
    public checkFlowType(): void {
        const page = this.store$.selectSignal(selectPageByRoute({ route: this.route }))();

        if (page?.page?.flowEntryPoint) {
            this.setQuoteFlowType(page);
        }
    }

    /**
     * Checks if the current page is the starting page of the quote flow.
     *
     * This method determines if the current page is the starting page by checking the active index
     * and the route properties. It returns true if the active index indicates the first section and page,
     * or if the section index is less than 1, or if the section or page route is not defined.
     *
     * @returns {boolean} - Returns true if the current page is the starting page, otherwise false.
     */
    public isStartingPage(): boolean {
        return (
            (this.activeIndex?.sectionIndex === 0 && this.activeIndex?.pageIndex === 0) ||
            this.activeIndex?.sectionIndex < 1 ||
            !this.route?.sectionRoute ||
            !this.route?.pageRoute
        );
    }

    /**
     * Update Page Config related signals
     * @param page The relevant page to set the values from
     */
    public updatePageConfig(page: any) {
        if (page != null) {
            this.hideBackButton.set(page.hideBackButton);
            this.hideTrackbar.set(page.hideTrackbar);
            this.showPremiumBanner.set(page.showPremiumBanner);
            this.slidePremiumBanner.set(page.slidePremiumBanner);
            this.promotionDisclaimer.set(page.promotionDisclaimer);
            this.minifyTrackbar.set(page.minifyTrackbar);
            this.applyTransparentHeader.set(page.applyTransparentHeader);
        }
    }

    /**
     * Config for Global Error Popup
     * @param errorConfig
     * @private
     */
    private errorConfig(errorConfig: any) {
        errorConfig.create = (
            title: string,
            message: string,
            status: number,
            showRetry: boolean,
        ) => {
            this.dispatchRequestFailure({ isErrored: true, message, status });

            return this.modalService.create({
                nzTitle: title,
                nzContent: ErrorDialogComponent,
                nzFooter: null,
                nzCentered: true,
                nzMaskClosable: !this.appConfig.config()?.production,
                nzClosable: !this.appConfig.config()?.production,
                nzData: {
                    message,
                    status,
                    showRetry,
                },
                nzKeyboard: false,
            });
        };

        errorConfig.instance.pipe(untilDestroyed(this)).subscribe((modal: any) => {
            const afterCloseSubscription = modal.afterClose.subscribe((_: any) => {
                errorConfig.isOpen = false;
                afterCloseSubscription.unsubscribe();
            });
        });
    }

    public navigate(
        route: any,
        condition?: any,
        keepQueryParams: boolean = true,
        queryParams?: Params,
    ): void {
        if (!queryParams) {
            queryParams = route ? route?.queryParams : {};
        }

        const pageData = this.activePage?.();

        if (pageData?.page?.isDropOffAnchorPoint && route?.baseRoute != 'callback') {
            this.anchorPointIsLoading$?.next(true);
            this.queueIsBusy$
                ?.pipe(takeUntil(this.anchorPointIsLoading$))
                .subscribe((isBusy: boolean) => {
                    if (!isBusy) {
                        this.completeNavigation(route, condition, keepQueryParams, queryParams);
                        this.anchorPointIsLoading$?.next(false);
                    }
                });
        } else {
            this.completeNavigation(route, condition, keepQueryParams, queryParams);
        }
    }

    /**
     * Completes the navigation to the given route.
     *
     * This method handles the final steps of navigation, including checking for drop-off conditions,
     * resolving conditional routes, and dispatching the navigation action to the store.
     *
     * @param {Route} route - The route to navigate to.
     * @param {Condition} [condition] - Optional condition to resolve the route.
     * @param {boolean} [keepQueryParams=true] - Whether to keep the current query parameters.
     * @param {Params} [queryParams] - Optional query parameters to include in the navigation.
     */
    public completeNavigation(
        route: Route,
        condition?: Condition,
        keepQueryParams: boolean = true,
        queryParams?: Params,
    ) {
        // Check if route specifies go to start OR
        // for empty state bug when navigating forward/back at start of flow

        if (
            route?.goToStart ||
            (isPlatformBrowser(this.platformId) &&
                route?.baseRoute !== '' &&
                this.activePage()?.section?.baseRoute !== '' &&
                !this.nextPage()?.page?.flowEntryPoint &&
                !this.leadNumber())
        ) {
            const quoteFlowType = route?.baseRoute as QuoteFlowType;

            this.route = route?.goToStart
                ? this.getStartingRoute()
                : this.getFlowTypeStartingRoute(quoteFlowType);

            this.store$.dispatch(
                navigateToRoute({
                    route: this.route,
                    keepQueryParams,
                    queryParams,
                }),
            );
            this.reset(false);
            return;
        }

        if (this.checkForDropOffs()) {
            return;
        }

        if (this.activePage()?.page?.components?.[0]?.selector) {
            const result = this.navigationService.resolveConditionalButtonRoute(
                this.activePage().page,
                route,
            );
            route = result.route;

            // Note: Button condition is being deprecated in favour of navigationService.resolveConditionalButtonRoute
            if (!result.resolvedRoute && condition && condition.routeArray?.length > 1) {
                if (!this.appConfig.config().production) {
                    console.error(
                        'button condition is deprecated, use navigationService.resolveConditionalButtonRoute instead.',
                    );
                }
                route = this.resolveStoreValueService$.resolveConditionalRoute(condition);
            }
        }

        if (route != null && !route?.baseRoute) {
            route = {
                ...route,
                baseRoute: this.route?.baseRoute,
            };
        }

        this.store$.dispatch(navigateToRoute({ route, keepQueryParams, queryParams }));
    }

    public reset(
        navToBrandsite: boolean = true,
        newTab: boolean = false,
        navToFirstPage = true,
    ): void {
        if (navToBrandsite) {
            if (newTab) {
                window.open(this.appConfig.config()?.brandSiteUrl, '_blank');
            } else {
                location.href = this.appConfig.config()?.brandSiteUrl;
            }
        }

        if (navToFirstPage) {
            this.route = this.getStartingRoute();
        }

        // if we don't have a lead number, then there is no need to reset the state
        // this helps for the scenario that we have different entry points
        // e.g. they land on the first page where they choose the quote vs they can land
        // directly on the car-insurance quote start page,
        if (this.leadNumber()) {
            this.store$.dispatch(
                reset({
                    initialRoute: this.route,
                    index: navToFirstPage ? null : this.activeIndex,
                    dropOffPageState: navToBrandsite
                        ? DropOffPageState.LOADING
                        : DropOffPageState.FORM,
                }),
            );
        }

        if (isPlatformBrowser(this.platformId)) {
            this.store$.dispatch(
                setABTestVariantsValues({
                    abTestVariants: window?.ab_test_variants,
                }),
            );
        }
    }

    back(): void {
        this.location.back();
    }

    /**
     * Get isDropOffAnchorPoint value from the active page
     * @returns {boolean} - Returns true if isDropOffAnchorPoint is explicitly set to true in CMS, false otherwise
     */
    public getDropOffAnchorPointValue(): boolean {
        const activePageData = this.activePage?.();
        return activePageData?.page?.isDropOffAnchorPoint ?? false;
    }

    protected abstract dispatchRequestFailure(payload: {
        isErrored: boolean;
        message: string;
        status: number;
    }): void;
    protected abstract createCanonicalLink(): void;
    public abstract getFlowTypeStartingRoute(flowType: QuoteFlowType): Route;
    public abstract goToDropOff(route?: string, additionalData?: any, code?: any): void;
    public abstract setQueryParams(queryParams?: Params): void;
    public abstract setHideTrackBar(isPageLoading: boolean): void;
    public abstract setShowPremiumBanner(isPageLoading: boolean): void;
    public abstract setQuoteFlowType(page: any): void;
    public abstract submitPageNavigationToDataLayer(event: NavigationEnd): void;
    public abstract checkForDropOffs(): boolean;
}
